import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { X, Loader2, Plus } from "lucide-react";
import { ForumRichEditor } from "@/components/site/ForumRichEditor";

/**
 * ComposeDiscussionModal — "Mulai diskusi" (Mockup #5).
 * Props:
 *   open, onClose(): visibility
 *   ruang: optional pre-selected Ruang doc (locks the Ruang dropdown when passed)
 *   onCreated(discussion): fired after successful POST /discussions
 *
 * Enforces mandatory Ruang tujuan (space) — no post can float free.
 */
export default function ComposeDiscussionModal({ open, onClose, ruang, onCreated }) {
  const [allRuang, setAllRuang] = useState([]);
  const [selectedId, setSelectedId] = useState(ruang?.ruang_id || "");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [tags, setTags] = useState([]);
  const [customTag, setCustomTag] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (ruang?.ruang_id) {
      setSelectedId(ruang.ruang_id);
    }
    api.get("/ruang").then((r) => setAllRuang(r.data.all || [])).catch(() => {});
  }, [open, ruang]);

  const selectedRuang = useMemo(() => {
    return ruang || allRuang.find((r) => r.ruang_id === selectedId) || null;
  }, [ruang, allRuang, selectedId]);

  // Suggest a few tag chips based on the selected ruang
  const suggestedTags = useMemo(() => {
    const map = {
      "sma": ["UTBK", "Beasiswa", "Jurusan", "Kesehatan Mental"],
      "smp": ["Adaptasi", "Pubertas", "Ekstrakurikuler"],
      "sd": ["Homework", "Pertemanan", "Kurikulum"],
      "persiapan-kuliah": ["IELTS", "SAT", "Beasiswa", "Jurusan"],
      "biaya": ["Uang Pangkal", "SPP", "Asuransi Pendidikan"],
      "parenting": ["Screen time", "Emosi", "Rutinitas"],
    };
    return map[selectedRuang?.slug] || ["Rekomendasi", "Pengalaman", "Tanya"];
  }, [selectedRuang]);

  const toggleTag = (t) => {
    setTags((prev) => (prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t]));
  };

  const addCustomTag = () => {
    const t = customTag.trim();
    if (!t) return;
    if (!tags.includes(t)) setTags((prev) => [...prev, t]);
    setCustomTag("");
  };

  // Strip tags for length validation so `<p>hi</p>` counts as 2 chars, not 8.
  const plainBody = (body || "").replace(/<[^>]+>/g, "").trim();
  const canSubmit = selectedId && title.trim().length >= 3 && plainBody.length >= 5 && !submitting;

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const r = await api.post("/discussions", {
        ruang_id: selectedId, title: title.trim(), body: body.trim(), tags,
      });
      toast.success("Diskusi Anda terbit di Ruang " + (r.data.discussion?.ruang_name || "") + ".");
      onCreated && onCreated(r.data.discussion);
      // reset
      setTitle(""); setBody(""); setTags([]); setCustomTag("");
      onClose && onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal mengirim diskusi.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 backdrop-blur-sm" role="dialog" aria-modal="true" data-testid="compose-modal">
      <div className="w-full sm:max-w-lg max-h-[92vh] overflow-y-auto bg-white rounded-t-3xl sm:rounded-3xl shadow-xl">
        {/* Dark header */}
        <div className="bg-brand-primary text-white px-5 py-4 flex items-center gap-3 sm:rounded-t-3xl">
          <button onClick={onClose} aria-label="Tutup" data-testid="compose-close-btn" className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center">
            <X className="w-5 h-5" />
          </button>
          <h2 className="font-serif-display text-xl">Mulai diskusi</h2>
        </div>

        <div className="p-5 space-y-5">
          {/* Ruang tujuan */}
          <div>
            <label className="eyebrow text-brand-text-muted mb-2 block">Ruang tujuan</label>
            {ruang ? (
              <div className="flex items-center gap-3 rounded-xl border-2 border-brand-primary/20 bg-brand-primary/5 px-4 py-3">
                <span className="text-2xl" aria-hidden>{ruang.icon}</span>
                <span className="font-semibold text-brand-text">{ruang.name}</span>
              </div>
            ) : (
              <select
                value={selectedId}
                onChange={(e) => setSelectedId(e.target.value)}
                data-testid="compose-ruang-select"
                className="w-full h-12 rounded-xl border-2 border-brand-border bg-white px-4 text-sm focus:border-brand-primary focus:outline-none"
              >
                <option value="">Pilih Ruang tujuan…</option>
                {allRuang.map((r) => (
                  <option key={r.ruang_id} value={r.ruang_id}>{r.icon} {r.name}</option>
                ))}
              </select>
            )}
          </div>

          {/* Judul */}
          <div>
            <label className="eyebrow text-brand-text-muted mb-2 block">Judul</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Judul diskusi Anda…"
              maxLength={200}
              data-testid="compose-title-input"
              className="w-full h-12 rounded-xl border-2 border-brand-border bg-white px-4 text-sm focus:border-brand-primary focus:outline-none"
            />
          </div>

          {/* Ceritakan */}
          <div>
            <label className="eyebrow text-brand-text-muted mb-2 block">Ceritakan</label>
            <ForumRichEditor
              value={body}
              onChange={setBody}
              placeholder="Tulis pertanyaan atau pengalaman Anda. Makin jelas, makin banyak orang tua yang bisa bantu…"
              testidPrefix="compose-body"
              minHeight={140}
            />
          </div>

          {/* Tags */}
          <div>
            <label className="eyebrow text-brand-text-muted mb-2 block">Tag topik (opsional)</label>
            <div className="flex flex-wrap gap-2">
              {suggestedTags.map((t) => {
                const on = tags.includes(t);
                return (
                  <button
                    key={t}
                    type="button"
                    onClick={() => toggleTag(t)}
                    data-testid={`compose-tag-${t}`}
                    className={`h-8 px-3 rounded-full text-xs font-medium border transition-colors ${
                      on ? "bg-brand-primary text-white border-brand-primary" : "bg-white text-brand-text-muted border-brand-border hover:border-brand-primary/40"
                    }`}
                  >
                    {on ? "" : "+ "}{t}
                  </button>
                );
              })}
              {/* Custom tag input */}
              <div className="inline-flex items-center gap-1">
                <input
                  type="text"
                  value={customTag}
                  onChange={(e) => setCustomTag(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCustomTag(); } }}
                  placeholder="Tag lain…"
                  className="h-8 rounded-full border border-brand-border bg-white px-3 text-xs w-24 focus:border-brand-primary focus:outline-none"
                />
                {customTag.trim() && (
                  <button type="button" onClick={addCustomTag} className="h-8 w-8 rounded-full bg-brand-primary text-white flex items-center justify-center" aria-label="Tambah tag">
                    <Plus className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
            {tags.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-3">
                {tags.map((t) => (
                  <span key={t} className="inline-flex items-center gap-1 h-7 px-2.5 rounded-full bg-brand-accent/10 text-brand-accent text-xs">
                    #{t}
                    <button onClick={() => toggleTag(t)} aria-label={`Hapus tag ${t}`}><X className="w-3 h-3" /></button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Helper */}
          {selectedRuang && (
            <div className="rounded-xl bg-brand-muted/60 border border-brand-border px-4 py-3 text-xs text-brand-text leading-relaxed" data-testid="compose-helper">
              💡 Diskusi ini akan tampil di Ruang <strong>{selectedRuang.name}</strong> dan di beranda anggotanya — bukan hilang di aliran umum.
            </div>
          )}

          {/* Submit */}
          <button
            onClick={submit}
            disabled={!canSubmit}
            data-testid="compose-submit-btn"
            className="w-full h-12 rounded-full bg-brand-accent hover:bg-brand-accent-hover disabled:bg-brand-accent/40 disabled:cursor-not-allowed text-white font-semibold transition-colors"
          >
            {submitting ? <span className="inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Mengirim…</span> : "Kirim diskusi"}
          </button>
        </div>
      </div>
    </div>
  );
}
